import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { catchError, distinctUntilChanged, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { ProgramariUltimaOraService } from './programari-ultima-ora.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/utils/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { SlotGrup, SlotGrupKey } from './programari-ultima-ora.interface';
import { AddProgramariUltimaOraComponent } from './add-programari-ultima-ora/add-programari-ultima-ora.component';
import { EditProgramariUltimaOraComponent } from './edit-programari-ultima-ora/edit-programari-ultima-ora.component';
import { GhiseuModuleService } from '../ghiseu-module/ghiseu-module.service';
import { LocatiiService } from '../locatii/locatii.service';
import { ServiciiService } from '../servicii/servicii.service';
import { map, finalize, Observable } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
    selector: 'app-programari-ultima-ora',
    templateUrl: './programari-ultima-ora.component.html',
    styleUrls: ['./programari-ultima-ora.component.scss']
})
export class ProgramariUltimaOraComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    private destroy$ = new Subject<void>();

    // Liste pentru dropdown-uri
    module: any[] = [];
    locatii: any[] = [];
    servicii: any[] = [];

    // Liste filtrate
    filteredModule: any[] = [];
    filteredLocatii: any[] = [];
    filteredServicii: any[] = [];

    // FormControls pentru căutare în select
    moduleControl = new FormControl();
    locatiiControl = new FormControl();
    serviciiControl = new FormControl();

    // Form pentru filtre
    searchParamsForm!: FormGroup;

    // Valori inițiale form
    initialFormValues = {
        id_modul: -1,
        id_locatie: -1,
        id_serviciu: -1
    };

    // URL pentru listare grupată
    dtLinkGrouped =
        environment.interop.basePath +
        'api/programari/nomenclatoare/ultima-ora-zi/list-dt-grouped';

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private programariSloturiUltimaOraService: ProgramariUltimaOraService,
        public permissionsService: PermissionsService,
        private ghiseuModuleService: GhiseuModuleService,
        private locatiiService: LocatiiService,
        private serviciiService: ServiciiService
    ) { }

    ngOnInit(): void {
        this.buildSearchParamsForm();
        this.loadModule();
        this.loadLocatii();
        this.loadServicii();
        this.renderFieldsSearch();
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            id_modul: new FormControl(-1),
            id_locatie: new FormControl(-1),
            id_serviciu: new FormControl(-1)
        });
    }

    renderFieldsSearch() {
        // Filter pentru module
        this.moduleControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredModule = this.module;
                    return of(this.module);
                }
                return this.filterModule(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredModule = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Filter pentru locații
        this.locatiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredLocatii = this.locatii;
                    return of(this.locatii);
                }
                return this.filterLocatii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredLocatii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Filter pentru servicii
        this.serviciiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredServicii = this.servicii;
                    return of(this.servicii);
                }
                return this.filterServicii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredServicii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterModule(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.module.filter(m =>
            this.normalizeString(m.name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterLocatii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.locatii.filter(l =>
            this.normalizeString(l.name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterServicii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.servicii.filter(s =>
            this.normalizeString(s.web_name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    onModuleSelectClosed() {
        const selectedValue = this.moduleControl.value;
        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredModule.some(m =>
                m.id === this.searchParamsForm.get('id_modul')?.value
            );
            if (!isSelectedInFilteredList) {
                this.moduleControl.setValue(null);
                this.filteredModule = this.module;
                this.destroy$.next();
                this.destroy$.complete();
                this.destroy$ = new Subject<void>();
                this.renderFieldsSearch();
            }
        }
    }

    onLocatiiSelectClosed() {
        const selectedValue = this.locatiiControl.value;
        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredLocatii.some(l =>
                l.id === this.searchParamsForm.get('id_locatie')?.value
            );
            if (!isSelectedInFilteredList) {
                this.locatiiControl.setValue(null);
                this.filteredLocatii = this.locatii;
                this.destroy$.next();
                this.destroy$.complete();
                this.destroy$ = new Subject<void>();
                this.renderFieldsSearch();
            }
        }
    }

    onServiciiSelectClosed() {
        const selectedValue = this.serviciiControl.value;
        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredServicii.some(s =>
                s.id === this.searchParamsForm.get('id_serviciu')?.value
            );
            if (!isSelectedInFilteredList) {
                this.serviciiControl.setValue(null);
                this.filteredServicii = this.servicii;
                this.destroy$.next();
                this.destroy$.complete();
                this.destroy$ = new Subject<void>();
                this.renderFieldsSearch();
            }
        }
    }

    loadModule() {
        this.isFilterLoading = true;
        this.ghiseuModuleService.getModule().pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.module = res;
                this.filteredModule = res;
            },
            error: (err) => {
                console.error('Error in get ghiseu module: ', err);
            }
        });
    }

    loadLocatii() {
        this.isFilterLoading = true;
        this.locatiiService.getLocatii().pipe(
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.locatii = res;
                this.filteredLocatii = res;
            },
            error: (err) => {
                console.error('Error in get locatii: ', err);
            }
        });
    }

    loadServicii() {
        this.isFilterLoading = true;
        this.serviciiService.getServicii().pipe(
            map((results: any) => [...results.data]),
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.servicii = res;
                this.filteredServicii = res;
            },
            error: (err) => {
                console.error('Error in get servicii: ', err);
            }
        });
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    applyFilters(): void {
        this.rerenderDT(true);
        this.showCard = false;
    }

    resetForm(): void {
        this.searchParamsForm.reset(this.initialFormValues);

        this.moduleControl.setValue(null);
        this.locatiiControl.setValue(null);
        this.serviciiControl.setValue(null);

        this.filteredModule = this.module;
        this.filteredLocatii = this.locatii;
        this.filteredServicii = this.servicii;

        this.destroy$.next();
        this.destroy$.complete();
        this.destroy$ = new Subject<void>();
        this.renderFieldsSearch();

        this.rerenderDT(true);
        this.showCard = true;
    }

    renderDT() {
        this.isLoading = true;
        const self = this;
        this.dtOptions = {
            pagingType: 'full_numbers',
            responsive: true,
            pageLength: 25,
            searching: false,
            lengthMenu: [10, 25, 50, 100],
            serverSide: true,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                // Adăugăm filtrele la parametrii
                const params = {
                    ...dataTablesParameters,
                    filters: this.searchParamsForm.value
                };

                this.http
                    .post<DataTablesResponse>(
                        this.dtLinkGrouped,
                        params
                    )
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'Modul',
                    data: 'denumire_modul',
                },
                {
                    title: 'Locație',
                    data: 'denumire_locatie',
                },
                {
                    title: 'Serviciu',
                    data: 'denumire_serviciu',
                },
                {
                    title: 'Zile configurate',
                    data: 'zile_formatate',
                    orderable: false,
                    render: (data: string) => {
                        if (!data) return '<span class="text-muted">-</span>';
                        return `<span class="badge-zile">${data}</span>`;
                    }
                },
                {
                    title: 'Email-uri',
                    data: 'emails',
                    render: (data: string) => {
                        if (!data) return '<span class="text-muted">-</span>';
                        const emails = data.split(';').filter(e => e.trim());
                        if (emails.length === 0) return '<span class="text-muted">-</span>';
                        if (emails.length === 1) return emails[0];
                        return `<span title="${data}">${emails[0]} (+${emails.length - 1})</span>`;
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    width: '80px',
                    render: (data: any, type: any, row: any, met: any) => {
                        return (
                            `
                            <div class="d-flex align-left justify-content-left">
                                <button type="button" class="action-btn edit ms-1" title="Editare grup">
                                    <img class="btn-img" src="/assets/icons/edit.svg" alt="edit"/>
                                </button>
                                <button type="button" class="action-btn delete ms-1" title="Șterge grup">
                                    <img class="btn-img" src="/assets/icons/delete.svg" alt="delete"/>
                                </button>
                            </div>
                            `
                        );
                    },
                },
            ],
            rowCallback: (row: Node, data: SlotGrup) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.ghiseu-settings.programari-ultima-ora.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditGroupDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.ghiseu-settings.programari-ultima-ora.delete'
                        )
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        this.deleteGroup(data);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(resetPaging = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(resetPaging);
        });
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddProgramariUltimaOraComponent, {
            ...this.dialogService.getDialogConfig(),
        });
        dialogRef.afterClosed().subscribe((result: { success: boolean }) => {
            if (result?.success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Configurare adăugată cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openEditGroupDialog(data: SlotGrup) {
        const dialogRef = this.dialog.open(EditProgramariUltimaOraComponent, {
            ...this.dialogService.getDialogConfig(),
            data: {
                ...data,
                isGroupEdit: true
            },
        });

        dialogRef.afterClosed().subscribe((result: { success: boolean }) => {
            if (result?.success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Grupul a fost actualizat cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteGroup(data: SlotGrup) {
        const zileCount = data.zile?.length || 0;
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            html: `Veți șterge <strong>${zileCount} configurări</strong> pentru:<br><br>
                   <strong>Modul:</strong> ${data.denumire_modul}<br>
                   <strong>Locație:</strong> ${data.denumire_locatie}<br>
                   <strong>Serviciu:</strong> ${data.denumire_serviciu}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.deleteGroupAction({
                    id_modul: data.id_modul,
                    id_locatie: data.id_locatie,
                    id_serviciu: data.id_serviciu
                });
            }
        });
    }

    deleteGroupAction(groupKey: SlotGrupKey) {
        this.isLoading = true;
        this.programariSloturiUltimaOraService.deleteGroup(groupKey).subscribe({
            next: () => {
                this.isLoading = false;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Grupul a fost șters cu succes!',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            error: (err) => {
                this.isLoading = false;
                let msg;
                try {
                    msg = err.error.errors.message[0];
                } catch (error) {
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.deleteGroupAction(groupKey);
                    }
                });
            }
        });
    }
}
